import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  CheckCircleOutlined,
  DeleteOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import {
  App,
  DatePicker,
  Flex,
  Input,
  Layout,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { ColumnsType } from "antd/lib/table";
import {
  useDeleteCommentMutation,
  useGetReportsQuery,
  useHideCommentMutation,
  useUnhideCommentMutation,
  useUpdateReportMutation,
} from "api/reports";
import { useCustomToken, useFilters, useHash, useQueryParams } from "hooks";
import { displayUserName, format, getRouteByName, palette } from "libs";
import {
  ReportInterface,
  ReportsStatuses,
  useGetUsersInfiniteQuery,
} from "api";
import { getErrorMessage, SmartSelectPagination } from "components";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import type { SerializedError } from "@reduxjs/toolkit";
import { enumKeys } from "types/object";
import { useAppSelector } from "store/hooks";

interface ReportsFiltersInterface {
  "filters[userId][$eq]"?: number[];
  "filters[postId][$eq]"?: number[];
  "filters[reporterId][$eq]"?: number[];
  "filters[status][$eq]"?: number;
  "filters[createdAt][$gt]"?: string;
  "filters[createdAt][$lte]"?: string;
  "filters[id][$eq]"?: string;
  page: number;
}

interface ReportsPageInterface {
  filterKey?: string;
}

export const ReportsPage = ({ filterKey }: ReportsPageInterface) => {
  const { userId, listingId } = useParams();
  const { notification } = App.useApp();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const queryParams = useQueryParams();
  const { token } = useCustomToken();

  const [updatingReport, setUpdatingReport] = useState<ReportInterface>();

  const { filters, onFiltersChange } = useFilters<ReportsFiltersInterface>(
    {
      "filters[userId][$eq]": undefined,
      "filters[postId][$eq]": undefined,
      "filters[reporterId][$eq]": undefined,
      "filters[status][$eq]": undefined,
      "filters[createdAt][$gt]": undefined,
      "filters[createdAt][$lte]": undefined,
      "filters[id][$eq]": undefined,
      page: 1,
    },
    { keyword: filterKey || "reports" }
  );

  const {
    isReportsRead,
    isUsersRead,
    isReportsEdit,
    isPostsRead,
    isCommentsEdit,
    isCommentsDelete,
    isAll,
  } = useAppSelector((state) => state.loggedAdmin.parsedPermissions);

  // Which comment a moderation action is currently in flight for, so only
  // that row shows a spinner instead of the whole table locking up.
  const [moderatingCommentId, setModeratingCommentId] = useState<number>();

  const [hideComment] = useHideCommentMutation();
  const [unhideComment] = useUnhideCommentMutation();
  const [deleteComment] = useDeleteCommentMutation();

  /**
   * Hide / reinstate a reported comment. Hiding is the default moderation
   * action because it is reversible: the comment leaves every reader-facing
   * surface but the row survives, so the report keeps rendering what was
   * acted on and a wrong call can be undone.
   */
  const onToggleCommentHidden = useCallback(
    async (commentId: number, hidden: boolean) => {
      setModeratingCommentId(commentId);
      try {
        await (hidden
          ? unhideComment(commentId)
          : hideComment(commentId)
        ).unwrap();
        notification.success({
          message: hidden
            ? "Comentariul a fost afisat din nou"
            : "Comentariul a fost ascuns",
        });
      } catch (e) {
        // `.unwrap()` rejects with exactly this union — TS just can't narrow a
        // catch binding, which is always `unknown`.
        notification.error({
          message: getErrorMessage(e as FetchBaseQueryError | SerializedError),
        });
      } finally {
        setModeratingCommentId(undefined);
      }
    },
    [hideComment, notification, unhideComment]
  );

  /** Hard delete — removes the comment and its replies for good. */
  const onDeleteComment = useCallback(
    async (commentId: number) => {
      setModeratingCommentId(commentId);
      try {
        await deleteComment(commentId).unwrap();
        notification.success({ message: "Comentariul a fost sters" });
      } catch (e) {
        // `.unwrap()` rejects with exactly this union — TS just can't narrow a
        // catch binding, which is always `unknown`.
        notification.error({
          message: getErrorMessage(e as FetchBaseQueryError | SerializedError),
        });
      } finally {
        setModeratingCommentId(undefined);
      }
    },
    [deleteComment, notification]
  );

  const { data, isLoading, isFetching } = useGetReportsQuery(filters);

  const [
    updateReportMutation,
    { isLoading: isUpdateReportLoading, isSuccess, error },
  ] = useUpdateReportMutation();

  useEffect(() => {
    if (!isReportsRead && !isAll) {
      navigate(getRouteByName("NotFoundPage"));
    }
  }, [isReportsRead, isAll]);

  useHash("reports");

  useEffect(() => {
    if (userId) {
      onFiltersChange({ "filters[userId][$eq]": [Number(userId)] });
    }
  }, [userId]);

  useEffect(() => {
    if (listingId) {
      onFiltersChange({ "filters[postId][$eq]": [Number(listingId)] });
    }
  }, [listingId]);

  useEffect(() => {
    if (updatingReport) {
      updateReportMutation({ id: updatingReport.id, status: 2 });
    }
  }, [updatingReport]);

  useEffect(() => {
    if (isSuccess) {
      notification.success({ message: "Reportul a fost rezolvat" });
    }
  }, [isSuccess]);

  useEffect(() => {
    if (error) {
      notification.error({ message: getErrorMessage(error) });
    }
  }, [error]);

  useEffect(() => {
    if (
      queryParams["filters[id][$eq]"] &&
      typeof queryParams["filters[id][$eq]"] === "string"
    ) {
      onFiltersChange({
        "filters[id][$eq]": queryParams["filters[id][$eq]"],
      });
    }
  }, [queryParams["filters[id][$eq]"]]);

  const usersColumns = useMemo<ColumnsType<ReportInterface>>(
    () => [
      {
        title: "Anunt",
        dataIndex: "post",
        render: (post: ReportInterface["post"]) => {
          // User-only and comment reports carry no post — the resource still
          // ships the object with null fields, so guard before linking.
          if (!post?.id) return "-";
          return isAll || isPostsRead ? (
            <Link
              to={{
                pathname: getRouteByName("ListingPage", { listingId: post.id }),
              }}
            >
              {post.title}
            </Link>
          ) : (
            post.title
          );
        },
      },
      {
        title: "Comentariu",
        dataIndex: "comment",
        width: 260,
        render: (comment: ReportInterface["comment"]) => {
          if (!comment) return "-";
          // Link targets the HOST thread — the admin reads the reported
          // comment in context, which is the only way to judge it.
          const body = (
            <Space size={4}>
              {comment.isHidden && <Tag color="orange">Ascuns</Tag>}
              <Typography.Text ellipsis style={{ maxWidth: 200 }}>
                {comment.body}
              </Typography.Text>
            </Space>
          );
          return isAll || isPostsRead ? (
            <Link
              to={{
                pathname: getRouteByName("ListingPage", {
                  listingId: comment.postId,
                }),
              }}
            >
              {body}
            </Link>
          ) : (
            body
          );
        },
      },
      {
        title: "Utilizator",
        dataIndex: "user",
        render: (user) =>
          isAll || isUsersRead ? (
            <Link to={getRouteByName("UserPage", { userId: user.id })}>
              {displayUserName(user)}
            </Link>
          ) : (
            <>{displayUserName(user)}</>
          ),
      },
      {
        title: "Reporter",
        dataIndex: "reporter",
        render: (reporter) =>
          isAll || isUsersRead ? (
            <Link to={getRouteByName("UserPage", { userId: reporter.id })}>
              {displayUserName(reporter)}
            </Link>
          ) : (
            <>{displayUserName(reporter)}</>
          ),
      },
      {
        title: "Spam",
        dataIndex: "hasSpam",
        render: (hasSpam) => (hasSpam ? "Da" : "Nu"),
      },
      {
        title: "Racism",
        dataIndex: "hasRacism",
        render: (hasRacism) => (hasRacism ? "Da" : "Nu"),
      },
      {
        title: "Pornografie",
        dataIndex: "hasPorn",
        render: (hasPorn) => (hasPorn ? "Da" : "Nu"),
      },
      {
        title: "Cuvinde necenzurte",
        dataIndex: "hasBadWords",
        render: (hasBadWords) => (hasBadWords ? "Da" : "Nu"),
      },
      {
        title: "Descriere",
        dataIndex: "description",
      },
      {
        title: "Statut",
        dataIndex: "status",
        render: (status) => t(ReportsStatuses[status]),
      },
      {
        title: "Data",
        dataIndex: "createdAt",
        width: 200,
        render: (createAt) => (
          <Space>
            <Typography.Text>{format(createAt)}</Typography.Text>
            <Typography.Text>{format(createAt, "HH:mm A")}</Typography.Text>
          </Space>
        ),
      },
      {
        fixed: "right",
        width: 150,
        align: "end",
        render: (_, report) => (
          <>
            {!filterKey && (
              <Space>
                {/* Comment moderation. Hiding comes first and is the action
                    to reach for: it is reversible, so a wrong call costs
                    nothing and the reported content stays available for
                    review. Delete is behind a confirm because it also takes
                    the whole reply subtree with it, permanently. */}
                {report.comment &&
                  (moderatingCommentId === report.comment.id ? (
                    <Spin />
                  ) : (
                    <>
                      {(isAll || isCommentsEdit) && (
                        <Tooltip
                          title={
                            report.comment.isHidden
                              ? "Afiseaza comentariul"
                              : "Ascunde comentariul"
                          }
                        >
                          {report.comment.isHidden ? (
                            <EyeOutlined
                              style={{ fontSize: "1rem", cursor: "pointer" }}
                              onClick={() =>
                                onToggleCommentHidden(report.comment!.id, true)
                              }
                            />
                          ) : (
                            <EyeInvisibleOutlined
                              style={{ fontSize: "1rem", cursor: "pointer" }}
                              onClick={() =>
                                onToggleCommentHidden(report.comment!.id, false)
                              }
                            />
                          )}
                        </Tooltip>
                      )}

                      {(isAll || isCommentsDelete) && (
                        <Popconfirm
                          title="Stergi comentariul?"
                          description="Se sterge definitiv, impreuna cu raspunsurile lui."
                          okText="Sterge"
                          okButtonProps={{ danger: true }}
                          cancelText="Renunta"
                          onConfirm={() => onDeleteComment(report.comment!.id)}
                        >
                          <Tooltip title="Sterge comentariul">
                            <DeleteOutlined
                              style={{
                                fontSize: "1rem",
                                cursor: "pointer",
                                color: token.colorError,
                              }}
                            />
                          </Tooltip>
                        </Popconfirm>
                      )}
                    </>
                  ))}

                {(isAll || isReportsEdit) &&
                  report.status === ReportsStatuses.PENDING && (
                    <>
                      {updatingReport?.id === report.id &&
                      isUpdateReportLoading ? (
                        <Spin />
                      ) : (
                        <CheckCircleOutlined
                          style={{
                            fontSize: "1rem",
                            cursor: "pointer",
                            color: token.primaryColor,
                          }}
                          onClick={() => setUpdatingReport(report)}
                        />
                      )}
                    </>
                  )}

                {(isAll || isPostsRead) && (
                  <Link
                    to={{
                      pathname: getRouteByName("ListingPage", {
                        listingId: report.id,
                      }),
                      hash: "#reports",
                    }}
                  >
                    <EyeOutlined
                      style={{ fontSize: "1rem", cursor: "pointer" }}
                    />
                  </Link>
                )}
              </Space>
            )}
          </>
        ),
      },
    ],
    [
      token,
      filterKey,
      updatingReport,
      isPostsRead,
      isReportsEdit,
      isCommentsEdit,
      isCommentsDelete,
      isUsersRead,
      moderatingCommentId,
      onDeleteComment,
      onToggleCommentHidden,
      isAll,
    ]
  );

  return (
    <>
      <Flex justify="space-between" style={{ marginBottom: "1rem" }}>
        <Space wrap>
          <Input.Search
            placeholder="Cauta id"
            value={filters["filters[id][$eq]"]}
            onChange={(val) =>
              onFiltersChange({ "filters[id][$eq]": val.target.value })
            }
            allowClear
          />

          {!userId && (
            <SmartSelectPagination
              query={useGetUsersInfiniteQuery}
              style={{ minWidth: 200 }}
              placeholder="Alege utilizator"
              value={filters["filters[userId][$eq]"]}
              onChange={(val) =>
                onFiltersChange({ "filters[userId][$eq]": val })
              }
              filterOption={false}
              allowClear
              // showSearch
            />
          )}

          <SmartSelectPagination
            query={useGetUsersInfiniteQuery}
            style={{ minWidth: 200 }}
            placeholder="Alege reporter"
            value={filters["filters[reporterId][$eq]"]}
            onChange={(val) =>
              onFiltersChange({ "filters[reporterId][$eq]": val })
            }
            filterOption={false}
            allowClear
            // showSearch
          />

          <Select
            placeholder="Statut"
            style={{ width: 125 }}
            value={filters["filters[status][$eq]"]}
            onChange={(val) => onFiltersChange({ "filters[status][$eq]": val })}
            allowClear
          >
            {enumKeys(ReportsStatuses).map((i) => (
              <Select.Option key={i} value={ReportsStatuses[i]}>
                {t(i)}
              </Select.Option>
            ))}
          </Select>

          <DatePicker.RangePicker
            defaultValue={
              filters["filters[createdAt][$gt]"] !== undefined
                ? [
                    dayjs(filters["filters[createdAt][$gt]"], "YYYY-MM-DD"),
                    dayjs(filters["filters[createdAt][$lte]"], "YYYY-MM-DD"),
                  ]
                : undefined
            }
            placeholder={["Creat de la", "Creat pina la"]}
            onChange={(date: any) => {
              onFiltersChange({
                "filters[createdAt][$gt]":
                  date && date[0]
                    ? date[0].format("YYYY-MM-DDT00:00Z")
                    : undefined,
                "filters[createdAt][$lte]":
                  date && date[1]
                    ? date[1].format("YYYY-MM-DDT23:59Z")
                    : undefined,
              });
            }}
          />
        </Space>
      </Flex>
      <Layout style={{ backgroundColor: palette.white }}>
        <Table
          columns={usersColumns}
          loading={isLoading || isFetching}
          dataSource={data?.data}
          rowKey="id"
          pagination={{
            total: data?.pagination.total,
            current: data?.pagination.currentPage,
            pageSize: data?.pagination.itemsPerPage,
            onChange: (page) => onFiltersChange({ page }),
            showSizeChanger: false,
          }}
          scroll={{ x: 1660 }}
        />
      </Layout>
    </>
  );
};
